import React, { useState, useEffect, useCallback  } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getWidgetViewsDetail,getWidgetViewsGrapghList } from "../utils/customerApi";
import CheckCurrentPlan from "../components/CheckCurrentPlan";

const AppWidgetViewDetail = ({ app_name, app_type, app_slug }) => {
    const [widgetViewDetailDetail, setWidgetViewDetailDetail] = useState({});
    const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);

    // Map app_type → new_app_slug
    const appSlugMap = {
        Google_Review: "review",
        Amazon_Review: "review",
        AliExpress_Review: "review",
        Yelp_Review: "review",
        G2_Review: "review",
        Hotels_Review: "review",
        AppStore_Review: "review",
        Capterra_Review: "review",
        Booking_Review: "review",
        Tripadvisor_Review: "review",
        Airbnb_Review: "review",
        Bbb_Review: "review",
        Etsy_Review: "review",
        Facebook_Review: "review",
        Open_Table_Review: "review",
        Instagram_Chat: "chat",
        Telegram_Chat: "chat",
        Line_Chat: "chat",
        Viber_Chat: "chat",
        Facebook_Chat: "chat",
        AI_ChatBot: "chat",
        Instagram_Feed: "instagram",
        Testimonial_Slider: "testimonial",
        Pdf_Embed: "file-embed",
        File_Embed: "file-embed",
    };

    // Get new slug, fallback to original
    const new_app_slug = appSlugMap[app_type] || app_slug;


    //Call API
    const fetchWidgetViewDetailData = useCallback(async () => {
        try { 
            const result = await getWidgetViewsDetail(app_type,new_app_slug);
            if (result?.status === true) {
                setWidgetViewDetailDetail(result.data);
            }
        } catch (error) {
            toast.error(`${error.message}`);
        } 
    }, [app_type, new_app_slug]);

    useEffect(() => {
        fetchWidgetViewDetailData();
    }, [fetchWidgetViewDetailData]);

    // calculate progress %
    const viewLimit = widgetViewDetailDetail?.assign_views_count || 0;
    const usedViews = widgetViewDetailDetail?.widget_views_count || 0;
    const progressPercent = viewLimit > 0 ? Math.min((usedViews / viewLimit) * 100, 100) : 0;
    
    // format resets_on
    const formatResetDate = (dateString) => {
        if (!dateString) return "--";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
    };
    
    // Month navigation state
    const today = new Date();
    const maxMonthIndex = today.getMonth(); 
    const maxYear = today.getFullYear();

    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [chartData, setChartData] = useState([]); 
    const [loading, setLoading] = useState(false);

    // Function to fetch API data
    const fetchMonthData = async (app_type,new_app_slug,year,monthIndex) => {
        try {
        setLoading(true);

        // Example API call
        const response = await getWidgetViewsGrapghList(app_type, new_app_slug, year, monthIndex + 1);

        if (response?.status === true) {
            setChartData(formatChartData(response.data, monthIndex, year));
        } else {
            toast.error("No data found for this month");
        }
        } catch (err) {
            toast.error("Error fetching data: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load current month dummy data on first render
    useEffect(() => {
        // Call API for new month
        fetchMonthData(app_type,new_app_slug, currentYear, currentMonth);
    }, []);

    // Calculate date range string
    const getDateRange = (monthIndex, year) => {
        const startDate = new Date(year, monthIndex, 1);
        const endDate = new Date(year, monthIndex + 1, 0); // Last day of the month
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    };

    const handlePrevMonth = () => {
        let newMonth = currentMonth - 1;
        let newYear = currentYear;
        if (newMonth < 0) {
            newMonth = 11;
            newYear -= 1;
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);

        // Call API for new month
        fetchMonthData(app_type,new_app_slug, newYear, newMonth);
    };

    const handleNextMonth = () => {
        let newMonth = currentMonth + 1;
        let newYear = currentYear;
        if (newMonth > 11) {
            newMonth = 0;
            newYear += 1;
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);

        // Call API for new month
        fetchMonthData(app_type,new_app_slug, newYear, newMonth);
    };

    // Transform chartData to include day name + month name
    const formatChartData = (data, monthIndex, year) => {
        return data.map(item => {
            const date = new Date(year, monthIndex, item.day);
            const monthName = date.toLocaleDateString("en-US", { month: "short" });
            return { ...item, label: `${monthName} ${item.day}` };
        });
    };

    return (
        <>
            <div className="paypal-right-one col-md-6 col-12"> 
                <div className="widget_counter">
                    <CheckCurrentPlan from_page={false} app_name={app_name} app_type={app_type} app_slug={app_slug}>
                        <button className="total_widget">
                            <span>Widgets <strong>{widgetViewDetailDetail?.created_widget_count || 0} / {widgetViewDetailDetail?.assign_widget_count || 0}</strong></span>
                        </button>
                    </CheckCurrentPlan>

                    <button className="widget_view widget_onlyview" onClick={() => setIsWidgetModalOpen(true)}>
                        <span className="widget_view_count">Views <strong> {widgetViewDetailDetail?.widget_views_count || 0} / {widgetViewDetailDetail?.assign_views_count || 0}</strong></span>
                        <div className="progress_bar"><div className="progress_bar_fill" style={{ width: `${progressPercent}%` }}></div></div>
                        <div className="widgetreset_date">Resets on {formatResetDate(widgetViewDetailDetail?.resets_on)} </div>
                    </button>
                </div>
            </div>

            {isWidgetModalOpen && (
                <div className="popup-overlay widget-views">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => setIsWidgetModalOpen(false)}>
                            <img src={`${process.env.PUBLIC_URL}/images/close.png`} alt="close-icon" />
                        </button>
                        <div className="modal-body p-2">
                            <h3 className="h4 mt-4">{app_name} View Stats</h3>
                            <p className="font_14">Each app has its own view limit according to its plan. Views are counted when your app is loaded on your website or by a share link and each load counts as one view. If you exceed this limit, your app will be temporarily deactivated.</p>
                            <div className="paypal_view">
                                <span>Views <strong>{widgetViewDetailDetail?.widget_views_count || 0} / {widgetViewDetailDetail?.assign_views_count || 0}</strong></span>
                                <span>Days to Reset <strong>{formatResetDate(widgetViewDetailDetail?.resets_on)}</strong></span>
                                <span>Subscription <strong>All Apps {widgetViewDetailDetail?.plan_name} Pack</strong></span>
                                <Link to={`/customer/apps/${app_slug}/pricing/single`} className="btn_green ms-4">Increase View Limit</Link>
                            </div>

                            <div style={{ marginBottom: "20px" }}>
                                <button onClick={handlePrevMonth} style={{
                                    backgroundColor: "#0ec12f",
                                    color: "#fff",
                                    border: "none",
                                    padding: "5px 10px",
                                    cursor: "pointer"
                                }}>←</button>
                                <span style={{ margin: "0 10px" }}>
                                    {getDateRange(currentMonth, currentYear)}
                                </span>
                                <button
                                    onClick={handleNextMonth}
                                    style={{
                                        marginLeft: "10px",
                                        backgroundColor: currentYear > maxYear || (currentYear === maxYear && currentMonth >= maxMonthIndex)
                                        ? "#ccc"  
                                        : "#0ec12f" 
                                        ,
                                        color: "#fff",
                                        border: "none",
                                        padding: "5px 10px",
                                        cursor: currentYear > maxYear || (currentYear === maxYear && currentMonth >= maxMonthIndex)
                                        ? "not-allowed"
                                        : "pointer"
                                    }}
                                    disabled={currentYear > maxYear || (currentYear === maxYear && currentMonth >= maxMonthIndex)}
                                    >→</button>
                            </div>
                            {loading ? (
                                <div style={{ textAlign: 'center', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <p className="h4">Loading data...</p>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="day" />
                                    <YAxis domain={[0, widgetViewDetailDetail?.assign_views_count || 1000]} />
                                    <Tooltip 
                                        formatter={(value) => [`${value}`, 'Daily Views']}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload.length > 0) {
                                                const item = payload[0].payload;
                                                return item.label;
                                            }
                                            return label;
                                        }}
                                    />
                                    <Bar dataKey="views" fill="#0ec12f" />
                                </BarChart>
                                </ResponsiveContainer>
                            )}

                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppWidgetViewDetail;